import { call, put, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { isBoolean } from 'lodash';
import * as actions from '../slices/smartCoolerProducts.slice';
import * as api from "@/services/smartCoolerProducts";

/**
 * Helper to extract error details for the Global Watcher.
 * Ensure this matches the helper used in your other saga files.
 */
const getErrorPayload = (error: any) => ({
  message: error?.response?.data?.message || error?.message || "An unexpected error occurred",
  statusCode: error?.response?.status || 500,
  messageDetails: error?.response?.data?.messageDetails || [],
  errorCode: error?.response?.data?.errorCode || "API_ERROR"
});

function* handleFetchSmartCoolerProducts(action: ReturnType<typeof actions.fetchSmartCoolerProducts>): SagaIterator {
  try {
    const response = yield call(api.fetchSmartCoolerProducts, action.payload);
    // Standardizing success check
    if (response?.data?.success || response?.status === 200) {
      yield put(actions.fetchSmartCoolerProductsSuccess(response.data));
    } else {
      yield put(actions.fetchSmartCoolerProductsFailure(response.data));
    }
  } catch (error: any) {
    yield put(actions.fetchSmartCoolerProductsFailure(getErrorPayload(error)));
  }
}

function* handleFetchSmartCoolerProductById(action: ReturnType<typeof actions.fetchSmartCoolerProductById>): SagaIterator {
  try {
    const response = yield call(api.fetchSmartCoolerProductById, action.payload);
    if (response?.data?.success || response?.status === 201) {
      yield put(actions.fetchSmartCoolerProductByIdSuccess(response.data));
    } else {
      yield put(actions.fetchSmartCoolerProductByIdFailure(response.data));
    }
  } catch (error: any) {
    yield put(actions.fetchSmartCoolerProductByIdFailure(getErrorPayload(error)));
  }
}

function* handleCreateSmartCoolerProduct(action: ReturnType<typeof actions.createSmartCoolerProduct>): SagaIterator {
  try {
    const response = yield call(api.createSmartCoolerProduct, action.payload);
    if (response?.data?.success || response?.status === 200 || response?.status === 201) {
      yield put(actions.createSmartCoolerProductSuccess(response.data));
    } else {
      yield put(actions.createSmartCoolerProductFailure(response.data));
    }
  } catch (error: any) {
    yield put(actions.createSmartCoolerProductFailure(getErrorPayload(error)));
  }
}

function* handleUpdateSmartCoolerProduct(action: ReturnType<typeof actions.updateSmartCoolerProduct>): SagaIterator {
  try {
    const { id, payload } = action.payload;
    const response = yield call(api.updateSmartCoolerProduct, id, payload);

    if (response?.data?.success || response?.status === 200) {
      yield put(actions.updateSmartCoolerProductSuccess(response.data));
    } else {
      yield put(actions.updateSmartCoolerProductFailure(response.data));
    }
  } catch (error: any) {
    yield put(actions.updateSmartCoolerProductFailure(getErrorPayload(error)));
  }
}

function* handleDeleteSmartCoolerProduct(action: ReturnType<typeof actions.deleteSmartCoolerProduct>): SagaIterator {
  try {
    const { smartCoolerProductId, transactionId } = action.payload;
    const response = yield call(api.deleteSmartCoolerProduct, smartCoolerProductId, transactionId);

    if (response?.data?.success || response?.status === 200) {
      
      yield put(actions.deleteSmartCoolerProductSuccess({
        id: smartCoolerProductId,
        ...response.data
      }));

    } else {
      yield put(actions.deleteSmartCoolerProductFailure(response.data));
    }
  } catch (error: any) {
    yield put(actions.deleteSmartCoolerProductFailure(getErrorPayload(error)));
  }
}

function* handleActivateSmartCoolerProduct(action: ReturnType<typeof actions.activateSmartCoolerProduct>): SagaIterator {
  try {
    const { smartCoolerProductId, transactionId } = action.payload;
    const response = yield call(api.activateSmartCoolerProduct, smartCoolerProductId, transactionId);

    if (response?.data?.success || response?.status === 200) {
      yield put(actions.activateSmartCoolerProductSuccess(response.data));
    } else {
      yield put(actions.activateSmartCoolerProductFailure(response.data));
    }
  } catch (error: any) {
    yield put(actions.activateSmartCoolerProductFailure(getErrorPayload(error)));
  }
}

export default function* smartCoolerProductsSaga() {
  yield takeLatest(actions.fetchSmartCoolerProducts.type, handleFetchSmartCoolerProducts);
  yield takeLatest(actions.fetchSmartCoolerProductById.type, handleFetchSmartCoolerProductById);
  yield takeLatest(actions.createSmartCoolerProduct.type, handleCreateSmartCoolerProduct);
  yield takeLatest(actions.updateSmartCoolerProduct.type, handleUpdateSmartCoolerProduct);
  yield takeLatest(actions.deleteSmartCoolerProduct.type, handleDeleteSmartCoolerProduct);
  yield takeLatest(actions.activateSmartCoolerProduct.type, handleActivateSmartCoolerProduct);
}